import styled, { css, keyframes } from 'styled-components';
import { NavLink } from 'react-router-dom';

import { Text } from '../../../../components/Text/Text';
import { FTUX_MATTE, FTUX_STAGE, SHORT_SCREEN } from '../../../../theme/ftux-tokens';
import {
  ANIM,
  COLORS,
  LUMIO_MOBILE_ANIM,
  MOBILE_NAV,
  MOBILE_NAV_ANIM,
  NAV_DESKTOP,
} from '../../../../theme/tokens';

export type DrawerPhase = 'closed' | 'open' | 'closing';

/* FTUX - Getting Started's phone map holds the drawer open (features/ftux/map/MobileMap) and marks <html> with this
   attribute meanwhile, until the drawer has closed. Then only: on a short screen, the rows sit tighter, so Lumio's
   words fit over them. The drawer is unchanged otherwise (the map's light behind the bar leaves as the drawer opens). */
export const FTUX_MAP_HOLD_ATTR = 'data-ftux-phone-map';
const FTUX_MAP_HOLD = `:root[${FTUX_MAP_HOLD_ATTR}]`;
const FTUX_MAP_ROW_GAP_PX = 4;

/* While the map's light lies behind the bar (features/ftux/map/MobileMap, its navigation bar part), it marks <html>
   with this attribute: the band's scrim stands aside, so the light reaches the screen's foot behind the pill. */
export const FTUX_BAR_LIGHT_ATTR = 'data-ftux-bar-light';
const FTUX_BAR_LIGHT = `:root[${FTUX_BAR_LIGHT_ATTR}]`;

/* Scroll fade mask for the "More" drawer — items dissolve into the blurred
   backdrop at whichever edge still has hidden content (alpha mask, so the fade
   is to the backdrop, not a colour). */
const DRAWER_FADE = '28px';
const drawerScrollMask = (top?: boolean, bottom?: boolean): string => {
  const topPart = top ? `transparent 0, #000 ${DRAWER_FADE}` : '#000 0';
  const bottomPart = bottom ? `#000 calc(100% - ${DRAWER_FADE}), transparent 100%` : '#000 100%';
  return `linear-gradient(to bottom, ${topPart}, ${bottomPart})`;
};

const overlayFadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const overlayFadeOut = keyframes`
  from { opacity: 1; }
  to { opacity: 0; }
`;

/* Per-item open/close stagger, unrolled per nth-child. This MUST cover every drawer item: an item with
   no rule keeps the base `.drawer-item { opacity: 0 }` and never fades in. That was the Settings bug —
   only 7 slots existed, but with Blog enabled there are 8 items, so Settings (the 8th) stayed invisible.
   Keep DRAWER_ITEM_SLOTS >= the number of DRAWER_ITEMS in MobileNav.tsx. */
const DRAWER_ITEM_SLOTS = 8;
const SLOTS = Array.from({ length: DRAWER_ITEM_SLOTS }, (_, k) => k + 1);

const openStagger = css`
  ${SLOTS.map(
    (i) => `
        .drawer-item:nth-child(${i}) {
          animation: 0.3s ease forwards;
          animation-name: open-item;
          animation-delay: ${((DRAWER_ITEM_SLOTS - i) * MOBILE_NAV_ANIM.staggerMs) / 1000}s;
        }
      `,
  ).join('')}
`;

const closeStagger = css`
  ${SLOTS.map(
    (i) => `
        .drawer-item:nth-child(${i}) {
          opacity: 1;
          transform: translateY(0);
          animation: 0.3s ease forwards;
          animation-name: close-item;
          animation-delay: ${((i - 1) * MOBILE_NAV_ANIM.staggerMs) / 1000}s;
        }
      `,
  ).join('')}
`;

/* Template `.drawer-overlay` (+ is-open / is-closing / is-closed states). */
export const DrawerOverlay = styled.div<{ $phase: DrawerPhase }>`
  position: fixed;
  inset: 0;
  z-index: 40;
  background: ${MOBILE_NAV.drawerOverlay};
  backdrop-filter: blur(${MOBILE_NAV.drawerBlur});

  /* FTUX - Getting Started's phone map holds the drawer: the page under it veiled as the map veils it everywhere (one
     overlay, phones and computers alike: the stage's dim over its matte frost). */
  ${FTUX_MAP_HOLD} & {
    background: ${FTUX_STAGE.veil};
    backdrop-filter: ${FTUX_MATTE.dark};
  }

  :root[data-ows-theme='light'][${FTUX_MAP_HOLD_ATTR}] & {
    backdrop-filter: ${FTUX_MATTE.light};
  }
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  align-items: flex-end;
  /* Top clearance (so a scrolling list doesn't run under the status bar) +
     bottom clearance for the pill — the drawer scrolls within what's left. */
  padding: calc(16px + env(safe-area-inset-top)) 0 128px 0;
  opacity: 0;
  pointer-events: none;

  @keyframes open-item {
    from { opacity: 0; transform: translateY(20px); }
    to { opacity: 1; transform: translateY(0); }
  }
  @keyframes close-item {
    from { opacity: 1; transform: translateY(0); }
    to { opacity: 0; transform: translateY(20px); }
  }

  ${({ $phase }) =>
    $phase === 'open' &&
    css`
      pointer-events: auto;
      animation: ${overlayFadeIn} ${MOBILE_NAV_ANIM.itemMs / 1000}s ease forwards;
      ${openStagger}
    `}

  ${({ $phase }) =>
    $phase === 'closing' &&
    css`
      pointer-events: none;
      opacity: 1;
      animation: ${overlayFadeOut} ${MOBILE_NAV_ANIM.itemMs / 1000}s ease
        ${MOBILE_NAV_ANIM.overlayOutDelayMs / 1000}s forwards;
      ${closeStagger}
    `}

  ${NAV_DESKTOP} {
    display: none;
  }

  .settings-gear {
    transition: transform ${MOBILE_NAV_ANIM.gearMs / 1000}s ease;
    ${({ $phase }) =>
      $phase === 'open'
        ? css`
            transform: rotate(90deg);
          `
        : css`
            transform: rotate(0deg);
            transition-delay: ${MOBILE_NAV_ANIM.gearReturnDelayMs / 1000}s;
          `}
  }
`;

/* Template `.drawer-menu`. Scrolls vertically when there are more pages than fit
   the screen; the pill / status-bar clearance now lives on the overlay padding. */
export const DrawerMenu = styled.div<{ $fadeTop?: boolean; $fadeBottom?: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 16px;
  padding: 0 32px 0 0;
  width: 100%;

  max-height: 100%;
  overflow-y: auto;
  overflow-x: hidden;
  -webkit-overflow-scrolling: touch;
  overscroll-behavior: contain;
  scrollbar-width: none;
  &::-webkit-scrollbar {
    width: 0;
    height: 0;
  }

  ${({ $fadeTop, $fadeBottom }) =>
    ($fadeTop || $fadeBottom) &&
    css`
      -webkit-mask-image: ${drawerScrollMask($fadeTop, $fadeBottom)};
      mask-image: ${drawerScrollMask($fadeTop, $fadeBottom)};
    `}

  ${SHORT_SCREEN} {
    ${FTUX_MAP_HOLD} & {
      gap: ${FTUX_MAP_ROW_GAP_PX}px;
    }
  }
`;

/* Template `.drawer-item`. */
export const DrawerItem = styled(NavLink).attrs({ className: 'drawer-item' })`
  height: 48px;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: flex-end;
  gap: 12px;
  text-decoration: none;
  cursor: pointer;
  opacity: 0;
  transform: translateY(20px);

  &.active,
  &:hover {
    .drawer-label {
      color: ${MOBILE_NAV.itemActive};
    }

    /* Glyph-component icons (Players / Wallet) recolour via their SVG paths… */
    path[fill] {
      fill: ${MOBILE_NAV.itemActive};
    }

    path[stroke] {
      stroke: ${MOBILE_NAV.itemActive};
    }

    /* …mask-based icons recolour via background-color. */
    .drawer-icon-mask {
      background-color: ${MOBILE_NAV.itemActive};
    }
  }
`;

export const DrawerLabel = styled(Text).attrs({ tag: 'span' as const, className: 'drawer-label' })`
  color: ${MOBILE_NAV.drawerLabel};
  font-size: 14px;
  font-weight: 700;
  font-style: normal;
  text-transform: uppercase;
  line-height: 143%;
`;

/* Template `.drawer-icon-bubble`. */
export const DrawerIconBubble = styled.span`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 8px;

  .drawer-icon svg {
    display: block;
    width: 24px;
    height: 24px;
  }

  .drawer-icon-mask {
    width: 24px;
    height: 24px;
    background-color: ${MOBILE_NAV.iconDefault};
  }
`;

/* Solid-black fill that sits DIRECTLY BENEATH the scrim — an absolute child of it
 * (`top: 100%`, so it begins at the scrim's bottom edge and extends off the bottom
 * of the screen, outside the scrim's own bounds). Normally off-screen; when iOS
 * shoves the fixed bar UP (re-revealing its toolbar / home indicator) it rides up
 * with the scrim and covers the strip that opens beneath it, so no page content or
 * browser grey shows through. Fades with the bar. */
export const ScrimBackfill = styled.div<{ $hidden?: boolean }>`
  position: absolute;
  left: 0;
  right: 0;
  top: 100%;
  height: calc(150px + env(safe-area-inset-bottom));
  background: ${COLORS.pageBg};
  opacity: ${({ $hidden }) => ($hidden ? 0 : 1)};
  transition: opacity ${MOBILE_NAV_ANIM.hideMs}ms ${ANIM.EASE};
  pointer-events: none;
`;

/* How far the bar slides up as it fades back in after Lumio closes. */
const BAR_REVEAL_LIFT = 14;

/* Template `.page-shadow`. The scrim (the gradient that fades content out behind
 * the pill) lives on a `::before` so it can fade independently of the pill's
 * slide on the hide-on-scroll interaction. The band itself is `pointer-events:
 * none` so taps fall through to the page everywhere except the pill (which
 * re-enables them) — important once the pill has slid away. */
export const PageShadow = styled.div<{ $hidden?: boolean; $lumioActive?: boolean }>`
  width: 100%;
  /* Extend the band (and lift the pill) by the bottom safe area so the scrim runs
   * all the way down behind the iOS toolbar / home-indicator strip — no grey gap —
   * while the pill still sits clear of the home indicator. */
  height: calc(150px + env(safe-area-inset-bottom));
  display: flex;
  align-items: flex-end;
  justify-content: center;
  position: fixed;
  left: 0;
  right: 0;
  bottom: 0;
  padding: 0 12px calc(20px + env(safe-area-inset-bottom)) 12px;
  z-index: 50;
  pointer-events: none;
  overflow:visible;

  /* Crossfade + slide the whole bar OUT as the Lumio overlay blooms in, and slide +
   * fade it back IN on close (timed by MainLayout to start once the overlay orb has
   * slid home). */
  opacity: ${({ $lumioActive }) => ($lumioActive ? 0 : 1)};
  transform: translateY(${({ $lumioActive }) => ($lumioActive ? BAR_REVEAL_LIFT : 0)}px);
  transition: opacity ${MOBILE_NAV_ANIM.lumioFadeMs}ms ${LUMIO_MOBILE_ANIM.FADE_EASE},
    transform ${MOBILE_NAV_ANIM.lumioFadeMs}ms ${LUMIO_MOBILE_ANIM.FADE_EASE};

  @media (prefers-reduced-motion: reduce) {
    transform: none;
    transition: opacity ${MOBILE_NAV_ANIM.lumioFadeMs}ms linear;
  }

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background: ${MOBILE_NAV.shadow};
    opacity: ${({ $hidden }) => ($hidden ? 0 : 1)};
    transition: opacity ${MOBILE_NAV_ANIM.hideMs}ms ${ANIM.EASE};
    pointer-events: none;

    ${FTUX_BAR_LIGHT} & {
      opacity: 0;
    }
  }

  ${NAV_DESKTOP} {
    display: none;
  }
`;

/* Template `.oh-bottom-menu`. Slides fully below the viewport when `$hidden`
 * (scrolling down); `pointer-events: auto` re-enables taps on the pill, which the
 * `pointer-events: none` scrim band suppresses. The translate over-shoots the
 * pill height + its bottom inset + the safe area so it clears the screen edge. */
export const BottomMenu = styled.div<{ $hidden?: boolean; $lumioActive?: boolean }>`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-around;
  padding: 8px 24px;
  border-radius: 99px;
  background: ${MOBILE_NAV.menuBg};
  /* Stop intercepting taps while it's faded out under the Lumio overlay. */
  pointer-events: ${({ $lumioActive }) => ($lumioActive ? 'none' : 'auto')};
  will-change: transform;
  transition: transform ${MOBILE_NAV_ANIM.hideMs}ms ${ANIM.EASE};
  transform: ${({ $hidden }) =>
    $hidden ? 'translateY(calc(100% + 64px + env(safe-area-inset-bottom)))' : 'translateY(0)'};

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

const bottomItemCss = css`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 4px;
  gap: 2px;
  cursor: pointer;
  user-select: none;
  color: ${MOBILE_NAV.item};
  text-align: center;
  font-size: 10px;
  font-weight: 500;
  text-decoration: none;
  line-height: 1;

  &.active,
  &:hover {
    color: ${MOBILE_NAV.itemActive};

    /* Mask-based icons recolour via background-color (a plain colour swap), not
       the inlined-SVG path fill/stroke the old approach needed. */
    .icon-mask {
      background-color: ${MOBILE_NAV.itemActive};
    }

    /* Inline glyphs (Website, Marketing) paint with currentColor. */
    .icon-glyph {
      color: ${MOBILE_NAV.itemActive};
    }
  }
`;

/* The bar while its drawer is open (MobileNav): the owner is in More, so the page's own door is not lit (More is). */
export const IN_MORE_ATTR = 'data-in-more';

export const BottomItem = styled(NavLink)`
  ${bottomItemCss}

  [${IN_MORE_ATTR}] &.active:not(:hover) {
    color: ${MOBILE_NAV.item};

    .icon-mask {
      background-color: ${MOBILE_NAV.iconDefault};
    }

    .icon-glyph {
      color: ${MOBILE_NAV.iconDefault};
    }
  }
`;

export const BottomToggle = styled.div`
  ${bottomItemCss}
`;

export const IconWrapper = styled.span`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;

  .icon-mask {
    width: 24px;
    height: 24px;
    background-color: ${MOBILE_NAV.iconDefault};
  }

  .icon-glyph {
    color: ${MOBILE_NAV.iconDefault};
  }
`;

export const ItemLabel = styled(Text).attrs({ tag: 'span' as const })``;
